/*
===============================================================================================================
                                 Copyright 2024-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import { RuleActionValues, RuleTypes } from '@/utilities/rules';

/**
 * Generates the 'in-store return' cards config, based on the company and assets data provided
 * @return A list of card config objects
*  @param {{companyData: object, assetsData: object, customTextData: object}} options
*/
export const generateReturnOptionsConfig = ({ companyData = {}, assetsData = {}, customTextData = {} } = {}) => {
  const { inStoreReturns = {}, outOfHomeReturns = {} } = assetsData ?? {};

  const getFieldById = (id) => companyData.fields?.find((field) => field.id === id) || {};

  const returnOptions = [
    {
      id: 'inStoreReturns',
      icon: {
        src: '/icons/in-store-locations.svg',
        alt: 'A store building',
      },
      label: {
        id: 'label',
        enabled: getFieldById('label').value,
        text: customTextData?.inStoreReturns?.label,
        defaultText: inStoreReturns.label,
      },
      title: {
        id: 'title',
        enabled: true,
        text: inStoreReturns.title,
      },
      description: {
        id: 'description',
        enabled: true,
        text: customTextData?.inStoreReturns?.description,
        defaultText: inStoreReturns.description,
      },
      footer: {
        id: 'footer',
        enabled: getFieldById('storeFooter').value,
        text: customTextData?.inStoreReturns?.footer,
        color: getFieldById('storeFooterBackground').value === true ? inStoreReturns.footerColor : null,
        defaultText: inStoreReturns.footer,
      },
    },
    {
      id: 'outOfHomeReturns',
      icon: {
        src: '/icons/drop-off-locations.svg',
        alt: 'A map pin',
      },
      title: {
        id: 'title',
        enabled: true,
        text: outOfHomeReturns.title,
      },

      description: {
        id: 'description',
        enabled: true,
        text: customTextData?.outOfHomeReturns?.description,
        defaultText: outOfHomeReturns.description,
      },
      footer: {
        id: 'footer',
        enabled: getFieldById('carrierDropOffFooter').value,
        text: customTextData?.outOfHomeReturns?.footer,
        defaultText: outOfHomeReturns.footer,
      },
    },
  ];
  return returnOptions;
};

export const generateDefaultReturnMethodRulePayload = ({
  existingRule,
  enabled,
  ruleType,
}) => {
  let rulePayload = {
    name: ruleType === RuleTypes.HOME_PICKUP ? 'Default home pick-up rule' : 'Default store drop-off rule',
    conditions: [{
      fact: 'product',
      operator: 'default',
      value: ['true'],
    }],
    isDefault: true,
    enabled,
    ruleType,
    eventData: {
      enabled: RuleActionValues.TRUE,
    },
  };

  if (existingRule) {
    rulePayload = {
      ...rulePayload,
      name: existingRule.ruleDescription,
      conditions: existingRule.rule.conditions.all,
      ruleId: existingRule.ruleId,
    };
  }

  return rulePayload;
};

export default {
  generateReturnOptionsConfig,
  generateDefaultReturnMethodRulePayload,
};
